import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ViolationChargeCard = ({ charge, onStatusUpdate }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return `$${Number(amount)?.toFixed(2)}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'paid':
        return 'bg-success/10 text-success border-success/20';
      case 'disputed':
        return 'bg-danger/10 text-danger border-danger/20';
      case 'refunded':
        return 'bg-muted-foreground/10 text-muted-foreground border-muted-foreground/20';
      case 'cancelled':
        return 'bg-muted/10 text-muted-foreground border-muted/20';
      default:
        return 'bg-muted/10 text-muted-foreground border-muted/20';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return 'Clock';
      case 'paid':
        return 'CheckCircle';
      case 'disputed':
        return 'AlertTriangle';
      case 'refunded':
        return 'RotateCcw';
      case 'cancelled':
        return 'XCircle';
      default:
        return 'Circle';
    }
  };

  const handleCancelCharge = async () => {
    if (!window.confirm('Are you sure you want to cancel this charge?')) {
      return;
    }

    try {
      setIsUpdating(true);
      await onStatusUpdate?.(charge?.id, 'cancelled');
    } catch (error) {
      console.error('Cancel charge error:', error);
      alert('Failed to cancel charge');
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="bg-card rounded-lg shadow-elevation-2 overflow-hidden hover:shadow-elevation-3 transition-smooth">
      {/* Header */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-2">
              <h3 className="font-body font-semibold text-foreground">
                {charge?.violationReason}
              </h3>
              <div className={`flex items-center space-x-1 px-2 py-1 rounded-full border text-xs font-medium ${getStatusColor(charge?.chargeStatus)}`}>
                <Icon name={getStatusIcon(charge?.chargeStatus)} size={12} strokeWidth={2} />
                <span className="capitalize">{charge?.chargeStatus}</span>
              </div>
            </div>
            <p className="font-body text-sm text-muted-foreground mb-2">
              Renter: {charge?.renter?.fullName || 'Unknown'}
            </p>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
              <div className="flex items-center space-x-1">
                <Icon name="FileText" size={14} strokeWidth={2} />
                <span className="font-body">{charge?.chargeReference}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Icon name="Calendar" size={14} strokeWidth={2} />
                <span className="font-body">{formatDate(charge?.createdAt)}</span>
              </div>
            </div>
          </div>

          <div className="text-right">
            <p className="font-heading text-2xl font-bold text-primary">
              {formatCurrency(charge?.chargeAmount)}
            </p>
            {charge?.chargeStatus === 'paid' && charge?.paidAt && (
              <p className="font-caption text-xs text-success mt-1">
                Paid {formatDate(charge?.paidAt)}
              </p>
            )}
          </div>
        </div>

        {/* Expand/Collapse Button */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="mt-3 flex items-center space-x-1 text-primary hover:text-primary/80 font-body text-sm font-medium transition-smooth"
        >
          <span>{isExpanded ? 'Hide Details' : 'View Details'}</span>
          <Icon
            name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
            size={16}
            strokeWidth={2}
          />
        </button>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="border-t border-border p-4 bg-muted/30 space-y-4">
          {/* Description */}
          <div>
            <h4 className="font-body font-semibold text-foreground mb-2">Description</h4>
            <p className="font-body text-sm text-muted-foreground whitespace-pre-wrap">
              {charge?.violationDescription}
            </p>
          </div>

          {/* Documentation */}
          {charge?.documentationUrls && charge?.documentationUrls?.length > 0 && (
            <div>
              <h4 className="font-body font-semibold text-foreground mb-2">Documentation</h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {charge?.documentationUrls?.map((url, index) => (
                  <a
                    key={index}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="aspect-video bg-muted rounded-lg overflow-hidden hover:ring-2 hover:ring-primary transition-smooth flex items-center justify-center"
                  >
                    {url?.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                      <img
                        src={url}
                        alt={`Documentation ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="flex flex-col items-center space-y-2">
                        <Icon name="FileText" size={32} className="text-primary" strokeWidth={2} />
                        <span className="font-caption text-xs text-muted-foreground">Document</span>
                      </div>
                    )}
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Dispute Information */}
          {charge?.chargeStatus === 'disputed' && charge?.disputeReason && (
            <div className="bg-danger/5 border border-danger/20 rounded-lg p-3">
              <div className="flex items-start space-x-2">
                <Icon name="AlertTriangle" size={16} className="text-danger flex-shrink-0 mt-0.5" strokeWidth={2} />
                <div>
                  <h4 className="font-body font-semibold text-danger mb-1">Disputed</h4>
                  <p className="font-body text-sm text-muted-foreground">
                    {charge?.disputeReason}
                  </p>
                  {charge?.disputeDate && (
                    <p className="font-caption text-xs text-muted-foreground mt-1">
                      Disputed on {formatDate(charge?.disputeDate)}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          {charge?.chargeStatus === 'pending' && (
            <div className="flex items-center justify-end space-x-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancelCharge}
                loading={isUpdating}
                iconName="XCircle"
              >
                Cancel Charge
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ViolationChargeCard;